import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TRecommendation } from '../../types/recommendation';
import {
  getMobileRecommendations,
  getDesktopRecommendations,
  TRecommendationsResult,
} from '../../services/getRecommendations';
import { ELoadingStatus } from '../../types/loadingStatus';
import { createAppAsyncThunk } from '../../utils/redux/createAppAsyncThunk';
import { DESKTOP_REDESIGN_RECOMMENDATIONS_EXPERIMENT } from '../../constants/experiments';
import { TAbUseExperimentsState } from '../../types/abUseExperiments';
import { isAbUseExperimentGroup } from '../../utils/isAbUseExperimentGroup';

const INITIAL_PAGE = 0;
const REDESIGN_EXPERIMENT_GROUP = 'B';

export enum EPaginationStatus {
  Idle = 'idle',
  Loading = 'loading',
  Error = 'error',
}

const initialState = {
  data: [] as TRecommendation[],
  status: ELoadingStatus.Loading,
  hasRequested: false,
  page: INITIAL_PAGE,
  hasMore: false,
  totalCount: null as number | null,
  paginationStatus: EPaginationStatus.Idle,
};

function isDesktopPaginationEnabled(isMobile: boolean, experiments: TAbUseExperimentsState) {
  return (
    !isMobile &&
    isAbUseExperimentGroup(experiments, DESKTOP_REDESIGN_RECOMMENDATIONS_EXPERIMENT, REDESIGN_EXPERIMENT_GROUP)
  );
}

export const fetchRecommendations = createAppAsyncThunk(
  'recommendations/fetch',
  (_, { getState, extra: context }) => {
    const {
      custom: { isMobile },
    } = context;
    const { regionId, landingType, liquiditySource, experiments = [] } = getState();
    const usePagination = isMobile || isDesktopPaginationEnabled(isMobile, experiments);
    const parameters = {
      regionId,
      landingType,
      ...(liquiditySource ? { liquiditySource } : null),
      ...(usePagination ? { page: INITIAL_PAGE } : null),
    };

    const getRecommendations = isMobile ? getMobileRecommendations : getDesktopRecommendations;

    return getRecommendations(context, { parameters, offset: INITIAL_PAGE });
  },
  {
    condition: (_, { getState }) => !getState().recommendations.hasRequested,
  },
);

type TFetchMoreRecommendationsPayload = TRecommendationsResult & {
  page: number;
};

export const fetchMoreRecommendations = createAppAsyncThunk(
  'recommendations/fetchMore',
  async (_, { getState, extra: context }) => {
    const {
      custom: { isMobile },
    } = context;
    const { regionId, landingType, liquiditySource, recommendations, experiments = [] } = getState();
    const useDesktopPagination = isDesktopPaginationEnabled(isMobile, experiments);

    if (!isMobile && !useDesktopPagination) {
      return {
        recommendations: [],
        hasMore: false,
        ...(recommendations.totalCount !== null && recommendations.totalCount !== undefined
          ? { totalCount: recommendations.totalCount }
          : null),
        page: recommendations.page,
      } as TFetchMoreRecommendationsPayload;
    }

    const nextPage = recommendations.page + 1;
    const parameters = {
      regionId,
      landingType,
      page: nextPage,
      ...(liquiditySource ? { liquiditySource } : null),
    };
    const response = isMobile
      ? await getMobileRecommendations(context, {
          parameters,
          offset: recommendations.data.length,
        })
      : await getDesktopRecommendations(context, {
          parameters,
          offset: recommendations.data.length,
        });

    return {
      ...response,
      page: nextPage,
    };
  },
  {
    condition: (_, { getState }) => {
      const { hasMore, paginationStatus } = getState().recommendations;

      return hasMore && paginationStatus !== EPaginationStatus.Loading;
    },
  },
);

type TToggleFavoritePayload = {
  offerId: number;
  isFavorite: boolean;
};

function setRecommendationPosition(recommendation: TRecommendation, position: number): TRecommendation {
  if (recommendation.analyticsData.type === 'trap' || recommendation.analyticsData.products[0].position === position) {
    return recommendation;
  }

  return {
    ...recommendation,
    analyticsData: {
      ...recommendation.analyticsData,
      products: [
        {
          ...recommendation.analyticsData.products[0],
          position,
        },
      ],
    },
  };
}

function appendUniqueRecommendations(
  currentRecommendations: TRecommendation[],
  addedRecommendations: TRecommendation[],
): TRecommendation[] {
  const offerIds = new Set<number>();
  const uniqueRecommendations: TRecommendation[] = [];

  currentRecommendations.forEach(({ offerId }) => {
    if (offerId !== null) {
      offerIds.add(offerId);
    }
  });

  addedRecommendations.forEach(recommendation => {
    const { offerId } = recommendation;

    if (offerId !== null) {
      if (offerIds.has(offerId)) {
        return;
      }

      offerIds.add(offerId);
    }

    uniqueRecommendations.push(
      setRecommendationPosition(recommendation, currentRecommendations.length + uniqueRecommendations.length + 1),
    );
  });

  return [...currentRecommendations, ...uniqueRecommendations];
}

const recommendationsSlice = createSlice({
  name: 'recommendations',
  reducers: {
    toggleFavorite: (state, action: PayloadAction<TToggleFavoritePayload>) => {
      state.data.forEach(recommendation => {
        if (recommendation.offerId === action.payload.offerId) {
          recommendation.isFavorite = action.payload.isFavorite;
        }
      });
    },
  },
  initialState,
  extraReducers(builder) {
    builder.addCase(fetchRecommendations.pending, state => {
      state.status = ELoadingStatus.Loading;
      state.hasRequested = true;
      state.paginationStatus = EPaginationStatus.Idle;
    });
    builder.addCase(fetchRecommendations.fulfilled, (state, { payload }) => {
      state.status = ELoadingStatus.Success;
      state.hasRequested = true;
      state.data = appendUniqueRecommendations([], payload.recommendations);
      state.page = INITIAL_PAGE;
      state.hasMore = payload.hasMore;
      state.totalCount = payload.totalCount ?? null;
      state.paginationStatus = EPaginationStatus.Idle;
    });
    builder.addCase(fetchRecommendations.rejected, state => {
      state.status = ELoadingStatus.Failure;
      state.hasRequested = false;
      state.hasMore = false;
      state.totalCount = null;
      state.page = INITIAL_PAGE;
      state.paginationStatus = EPaginationStatus.Idle;
    });
    builder.addCase(fetchMoreRecommendations.pending, state => {
      state.paginationStatus = EPaginationStatus.Loading;
    });
    builder.addCase(fetchMoreRecommendations.fulfilled, (state, { payload }) => {
      const recommendations = appendUniqueRecommendations(state.data, payload.recommendations);
      const hasNewRecommendations = recommendations.length > state.data.length;

      state.data = recommendations;
      state.page = payload.page;
      state.hasMore = hasNewRecommendations && payload.hasMore;
      state.totalCount = payload.totalCount ?? state.totalCount;
      state.paginationStatus = EPaginationStatus.Idle;
    });
    builder.addCase(fetchMoreRecommendations.rejected, state => {
      state.paginationStatus = EPaginationStatus.Error;
    });
  },
});

export const recommendationsReducer = recommendationsSlice.reducer;
export const { toggleFavorite } = recommendationsSlice.actions;
